import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Navbar1 } from "@/components/ui/navbar-1";
import { SiteFooter } from "@/components/SiteFooter";
import { listPublishedPosts, type BlogPost } from "@/lib/blog.functions";
import { CATEGORIES, CATEGORY_LABELS, type BlogCategory } from "@/lib/blog-topics";
import { cn } from "@/lib/utils";

const TITLE = "Blog Viny Club | Guias, história e mercado de vinil no Brasil";
const DESC =
  "Blog para colecionadores de vinil: dicas de garimpo, cuidados com discos, história dos selos brasileiros, análise de mercado e perfis de artistas. Atualizado toda semana.";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://viny.club/blog" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://viny.club/blog" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Blog Viny Club",
          url: "https://viny.club/blog",
          description: DESC,
        }),
      },
    ],
  }),
  loader: () => listPublishedPosts({ data: { limit: 60 } }),
  component: BlogIndex,
  errorComponent: () => (
    <div className="flex min-h-screen items-center justify-center px-4 text-center text-muted-foreground">
      Não foi possível carregar o blog agora.
    </div>
  ),
  notFoundComponent: () => null,
});

function BlogIndex() {
  const initial = Route.useLoaderData() as BlogPost[];
  const [cat, setCat] = useState<BlogCategory | "todas">("todas");
  const { data: posts = initial } = useQuery<BlogPost[]>({
    queryKey: ["blog", cat],
    queryFn: () =>
      listPublishedPosts({
        data: { limit: 60, category: cat === "todas" ? undefined : cat },
      }),
    initialData: cat === "todas" ? initial : undefined,
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar1 />
      <main className="mx-auto max-w-6xl px-6 pt-28 pb-16">
        <header className="mb-10">
          <p className="mb-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Blog
          </p>
          <h1 className="text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
            Coluna do colecionador
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground">
            Dicas de garimpo, cuidados com discos, história dos selos
            brasileiros, análise de mercado e perfis de artistas.

          </p>
        </header>

        <nav className="mb-10 flex flex-wrap gap-2">
          <CatChip active={cat === "todas"} onClick={() => setCat("todas")}>
            Todas
          </CatChip>
          {CATEGORIES.map((c) => (
            <CatChip key={c} active={cat === c} onClick={() => setCat(c)}>
              {CATEGORY_LABELS[c]}
            </CatChip>
          ))}
        </nav>

        {posts.length === 0 ? (
          <p className="py-24 text-center text-sm text-muted-foreground">
            Nenhum artigo por aqui ainda.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((p, i) => (
              <PostCard key={p.id} post={p} featured={i === 0 && cat === "todas"} />
            ))}
          </ul>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function CatChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-full border px-4 py-1.5 text-xs font-medium transition",
        active
          ? "border-white/80 bg-white text-black"
          : "border-white/10 bg-black/40 text-white/70 hover:border-white/30 hover:text-white",
      )}
    >
      {children}
    </button>
  );
}

function PostCard({ post, featured }: { post: BlogPost; featured?: boolean }) {
  return (
    <li className={cn(featured && "sm:col-span-2 lg:col-span-3")}>
      <Link
        to="/blog/$slug"
        params={{ slug: post.slug }}
        className="group block"
      >
        <div
          className={cn(
            "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]",
            featured ? "aspect-[21/9]" : "aspect-[16/10]",
          )}
        >
          {post.cover_url ? (
            <img
              src={post.cover_url}
              alt={post.title}
              className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.02]"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
              Viny Club
            </div>
          )}
        </div>
        <div className="mt-4">
          <div className="mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <span>{CATEGORY_LABELS[post.category]}</span>
            <span aria-hidden>·</span>
            <span>{post.reading_time} min</span>
          </div>
          <h2
            className={cn(
              "font-medium leading-snug tracking-tight text-foreground group-hover:opacity-80",
              featured ? "text-2xl sm:text-3xl" : "text-lg",
            )}
          >
            {post.title}
          </h2>
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
            {post.excerpt}
          </p>
        </div>
      </Link>
    </li>
  );
}
